import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Search, Edit, Trash2, ChevronLeft, ChevronRight } from 'lucide-react'
import toast from 'react-hot-toast'
import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useAuthStore } from '@/store/auth.store'
import { empAddressService } from '@/services/empAddressService'
import type { EmpAddressListItem } from '@/api/types'
import { showDeleteConfirm, showError, showSuccess } from '@/lib/swal'

export const EmpAddressList: React.FC = () => {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const branchId = user?.selectedBranch?.branchId

  const [addresses, setAddresses] = useState<EmpAddressListItem[]>([])
  const [loading, setLoading] = useState(false)
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [totalRecords, setTotalRecords] = useState(0)
  const [searchQuery, setSearchQuery] = useState('')
  const [deletingId, setDeletingId] = useState<number | null>(null)

  useEffect(() => {
    if (!branchId) return
    const load = async () => {
      try {
        setLoading(true)
        const res = await empAddressService.getAll(branchId, pageNumber, pageSize)
        setAddresses(res.data ?? [])
        setTotalRecords(res.totalRecords ?? 0)
      } catch (e: any) {
        const status = e?.response?.status
        const msg = (e?.response?.data?.message ?? e?.message ?? '').toLowerCase()
        const isNoData = status === 404 || /no data|not found|no records/.test(msg)
        setAddresses([])
        setTotalRecords(0)
        if (!isNoData) {
          toast.error(e?.response?.data?.message ?? 'Failed to load addresses.')
        }
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [branchId, pageNumber, pageSize])

  const filteredAddresses = useMemo(() => {
    if (!searchQuery.trim()) return addresses
    const q = searchQuery.toLowerCase()
    return addresses.filter(
      (a) =>
        String(a.homePhoneNumber ?? '').toLowerCase().includes(q) ||
        (a.addressDetail ?? '').toLowerCase().includes(q)
    )
  }, [addresses, searchQuery])

  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize))

  const formatDate = (d?: string) => {
    if (!d) return '—'
    try {
      return new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' })
    } catch {
      return String(d).slice(0, 10)
    }
  }

  const handleAdd = () => navigate('/emp-addresses/new')

  const handleEdit = (id: number) => navigate(`/emp-addresses/${id}/edit`)

  const handleDelete = async (id: number) => {
    const confirmed = await showDeleteConfirm('Delete Address', 'Are you sure you want to delete this address?')
    if (!confirmed) return
    setDeletingId(id)
    try {
      await empAddressService.delete(id)
      await showSuccess('Deleted', 'Address deleted successfully.')
      if (branchId) {
        const res = await empAddressService.getAll(branchId, pageNumber, pageSize)
        setAddresses(res.data ?? [])
        setTotalRecords(res.totalRecords ?? 0)
      }
    } catch (e: any) {
      await showError('Delete failed', e?.response?.data?.message ?? e?.message ?? 'Failed to delete address.')
    } finally {
      setDeletingId(null)
    }
  }

  if (!branchId) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader title="Employee Addresses" description="Manage employee addresses." />
        <Card>
          <CardContent className="p-6 text-center text-muted-foreground">
            Please select a branch first.
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title="Employee Addresses"
        description="View and manage employee addresses."
        action={{ label: 'New', onClick: handleAdd, icon: Plus }}
      />

      {/* Addresses list */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="px-3 text-left">Home Phone Number</TableHead>
                  <TableHead className="px-3 text-left">Address Detail</TableHead>
                  <TableHead className="px-3 text-left">Created Date</TableHead>
                  <TableHead className="px-3 w-[100px] text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : filteredAddresses.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                      No data available in table
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredAddresses.map((addr) => (
                    <TableRow key={addr.id}>
                      <TableCell className="px-3 text-left">
                        {addr.homePhoneNumber ?? '—'}
                      </TableCell>
                      <TableCell className="px-3 max-w-[240px] truncate text-left">
                        {addr.addressDetail ?? '—'}
                      </TableCell>
                      <TableCell className="px-3 text-left">
                        {formatDate(addr.createdDate)}
                      </TableCell>
                      <TableCell className="px-3 text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(addr.id!)}
                            className="h-8 w-8 p-0"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(addr.id!)}
                            disabled={deletingId === addr.id}
                            className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-4 mt-4 text-sm text-muted-foreground">
            <div>
              Showing {(pageNumber - 1) * pageSize + 1} to{' '}
              {Math.min(pageNumber * pageSize, totalRecords)} of {totalRecords} entries
            </div>
            <div className="flex items-center gap-2">
              <Select
                value={String(pageSize)}
                onValueChange={(v) => {
                  setPageSize(Number(v))
                  setPageNumber(1)
                }}
              >
                <SelectTrigger className="h-8 w-[80px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[10, 25, 50].map((size) => (
                    <SelectItem key={size} value={String(size)}>
                      {size}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  disabled={pageNumber <= 1}
                  onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span>
                  {pageNumber} / {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  disabled={pageNumber >= totalPages}
                  onClick={() => setPageNumber((p) => p + 1)}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

